import { useState } from 'react';

export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('companyName', formData.companyName);
      formDataToSend.append('contactName', formData.contactName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d47ukgtt5hvomr7ccrpg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          message: ''
        });
        setPrivacyAgreed(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrivacyAgree = async () => {
    const privacyFormData = new FormData();
    privacyFormData.append('agreed', 'true');
    privacyFormData.append('timestamp', new Date().toISOString());

    try {
      await fetch('https://readdy.ai/api/form/d47l5nrq5e24j8usdac0', {
        method: 'POST',
        body: privacyFormData
      });
    } catch (error) {
      console.error('Privacy agreement submission failed:', error);
    }

    setPrivacyAgreed(true);
    setShowPrivacyModal(false);
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-wide mb-2">CONTACT</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              お問い合わせ
            </h2>
            <div className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              <p className="mb-2 leading-tight">
                ワンストップでお気軽にお問い合わせください。
              </p>
              <p className="leading-tight">
                専門スタッフが迅速に対応いたします。
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="株式会社〇〇"
                  />
                </div>

                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                    ご担当者名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="山田 太郎"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="example@company.co.jp"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              <div className="text-center space-y-4">
                <div className="text-sm text-gray-600">
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
                  >
                    プライバシーポリシー
                  </button>
                  をご確認の上、同意いただける場合は送信ボタンを押してください。
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white px-8 py-4 rounded-[10px] text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <i className="ri-check-circle-fill text-green-500 text-xl mr-2"></i>
                    <span className="text-green-800 font-medium">送信完了</span>
                  </div>
                  <p className="text-green-700 text-sm">
                    お問い合わせありがとうございます。担当者より2営業日以内にご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <i className="ri-error-warning-fill text-red-500 text-xl mr-2"></i>
                    <span className="text-red-800 font-medium">送信エラー</span>
                  </div>
                  <p className="text-red-700 text-sm">
                    申し訳ございません。送信に失敗しました。しばらくしてから再度お試しください。
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">プライバシーポリシー</h3>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="text-sm text-gray-700 space-y-6">
                <div>
                  <h4 className="font-semibold text-base mb-3">個人情報の取り扱いについて</h4>
                  <p className="leading-relaxed">
                    ナインバレー株式会社（以下「当社」）は、お客様の個人情報を以下の通り取り扱います。
                  </p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">1. 個人情報の利用目的</h5>
                  <div className="leading-relaxed">
                    当社は、収集した個人情報を以下の目的で利用いたします：
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                      <li>お問い合わせへの回答およびご連絡</li>
                      <li>サービスのご提案およびご案内</li>
                      <li>各種資料の送付</li>
                      <li>アフターサービスの提供</li>
                      <li>統計データの作成（個人を特定できない形式）</li>
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">2. 個人情報の第三者提供</h5>
                  <p className="leading-relaxed">
                    当社は、以下の場合を除き、お客様の同意なく個人情報を第三者に提供することはありません：
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>法令に基づく場合</li>
                    <li>人の生命、身体または財産の保護のために必要がある場合</li>
                    <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">3. 個人情報の管理</h5>
                  <p className="leading-relaxed">
                    当社は、個人情報の漏洩、滅失、毀損の防止その他個人情報の安全管理のために必要かつ適切な措置を講じます。また、個人情報を取り扱う従業員に対して、適切な監督を行います。
                  </p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">4. 個人情報の開示・訂正・削除</h5>
                  <p className="leading-relaxed">
                    お客様は、当社が保有する個人情報について、開示、訂正、削除を求めることができます。これらのご請求については、下記のお問い合わせ先までご連絡ください。
                  </p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">5. Cookie等の利用</h5>
                  <p className="leading-relaxed">
                    当社のウェブサイトでは、サービス向上のためCookieを使用する場合があります。Cookieの使用を希望されない場合は、ブラウザの設定で無効にすることができます。
                  </p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">6. プライバシーポリシーの変更</h5>
                  <p className="leading-relaxed">
                    当社は、法令の変更等に伴い、本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、当社ウェブサイトに掲載した時点から効力を生じるものとします。
                  </p>
                </div>
                
                <div className="border-t pt-4">
                  <h5 className="font-medium text-gray-900 mb-2">お問い合わせ先</h5>
                  <div className="leading-relaxed">
                    個人情報の取り扱いに関するお問い合わせは、下記までご連絡ください：<br/>
                    <strong>ナインバレー株式会社</strong><br/>
                    〒753-0056　山口県山口市湯田温泉2丁目4-26<br/>
                    電話：083-902-5361<br/>
                    メール：info@ninevalley.co.jp
                  </div>
                </div>
                
                <div className="text-right text-xs text-gray-500 border-t pt-4">
                  制定日：2024年1月1日<br/>
                  最終更新日：2024年12月1日
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowPrivacyModal(false)}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-[10px] font-medium hover:bg-blue-700 transition-colors cursor-pointer"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}